'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * ObservationList — a net-new, Solr-backed, filterable observation list for the config app. Replaces the generic
 * pict-section-recordset list at #/PSRS/Observation/List (which HANGS scanning the ~21k-row Observations table) with
 * the fast `POST /1.0/ObservationsFilter/Index/{begin}/{cap}` search index Fieldbook uses (~70ms; the response's
 * {numFound, docs} carries type/date/author/project/artifact/doc-count so no per-row fetch is needed). Rows link to the
 * bespoke Observation workspace. Filters mirror the Fieldbook observation-list filter bar (v1): free-text, observation
 * types, authors, date range, in-reports (document count), project scope. Labels/tags (grouped by tag-type) are a
 * follow-up. This provider owns the fetch + the host-div paint; a thin ObservationList view supplies the host chrome.
 */
class ObservationListProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._mount = '#ObservationList-Host';
		this._filter = { SearchText: '', Types: [], Authors: [], AuthorNames: {}, From: '', To: '', Inclusion: '', IDProject: 0, ProjectName: '', Labels: {}, MatchAllLabels: false };
		this._projectTagTypes = null;   // the selected project's tag-type vocabulary (Labels filter is project-scoped)
		this._activeTagType = '';       // which tag type the label-value picker searches
		this._page = 0;
		this._cap = 25;
		this._total = 0;
		this._rows = [];
		this._authorNames = {};
		this._typeOptions = null;    // [{Label, Names, Count}] — cached once grouped (see _typeGrouped)
		this._typeGrouped = false;   // true once the catalog mapped types to DisplayNames (else retry next load)
		this._loading = false;
		this._error = '';
		this._loaded = false;

		// --- view mode (one filter bar, three bodies): 'list' (paginated) | 'charts' (facet panels) | 'map' (next) ---
		this._viewMode = 'list';
		this._baseline = 0;                       // unfiltered customer total (KPI context; captured in _ensureTypeOptions)
		this._chartTypeCounts = {};               // RecordSubType -> count under the current (minus-type) filter
		this._rep = { b0: 0, b1: 0, b2plus: 0 };  // DocumentCount buckets under the current (minus-reporting) filter
		this._chartLatencyMs = 0;
		this._typeChartOrder = null;              // stable chart-bar order (fixed on first charts load, count desc)
		this._chartsLoaded = false;
		this._prevKPI = null;                     // previous KPI values for number tweening

		// --- map view (Leaflet + docked responsive sidebar; the same pattern as the bridge dashboard). The map plots
		// the most-recent _MAP_FETCH matches of the SHARED filter that carry a capture/user coordinate; clicking a point
		// shows that observation's detail in the docked side panel IN PLACE (no shell repaint) so the map never resets. ---
		this._map = null;                         // Leaflet map instance (window.L)
		this._mapMarkers = [];                    // [{ r, m, base }] — r = { doc, id, pt }, m = circleMarker, base = color
		this._mapRows = [];                       // fetched docs decorated with { doc, id, pt }
		this._mapMappable = [];                   // the subset of _mapRows that have a plottable coordinate
		this._mapFetched = 0;                     // how many docs we actually fetched (<= _MAP_FETCH)
		this._mapLoaded = false;                  // true once a map fetch has completed at least once
		this._mapDetail = 0;                      // IDRecord shown in the docked side panel (0 = legend/hint)
		this._mapDetailObs = null;                // the full hydrated Observation (with .Details) for _mapDetail, once fetched
		this._mapDetailObsID = 0;                 // which id _mapDetailObs holds (guards stale async loads)
		this._mapDetailSpec = null;               // its resolved ObservationSpecification (for schema-driven columns)
		this._mapDetailLoading = false;           // true while fetching the full observation for the side panel
		this._mapView = null;                     // { center, zoom } preserved across benign remounts (dropdown close, etc.)
		this._mapFitPending = false;              // true after a data load → the next mount fits the bounds; a benign repaint keeps the view
		this._typeColorByRST = null; this._typeColorForOptions = null;  // cached RecordSubType -> chart-palette color

		// --- embed mode: when >0, this screen is embedded in a Project workspace tab, locked to that project (the
		// Project filter is hidden and IDProject is forced) — see openInto()/exitEmbed(). ---
		this._embeddedProjectID = 0;
		this._typeDropdownOpen = false;           // Observation Types dropdown-checklist open/closed
		this._fullscreen = false;                 // standalone "full screen" — overlay the whole browser view (see toggleFullscreen)

		// Close the type dropdown on an outside click (capture phase so it runs before the button's own handler).
		if (typeof document !== 'undefined')
		{
			this._onDocClick = (pEvent) =>
			{
				if (!this._typeDropdownOpen) { return; }
				try { const dd = document.querySelector('.obl-typedd'); if (dd && !dd.contains(pEvent.target)) { this._typeDropdownOpen = false; this.render(); } } catch (pError) { /* */ }
			};
			document.addEventListener('click', this._onDocClick, true);
			// Esc leaves full screen (routed through toggleFullscreen so it gets the same repaint/map handling).
			this._onKeydown = (pEvent) => { if (pEvent.key === 'Escape' && this._fullscreen) { this.toggleFullscreen(); } };
			document.addEventListener('keydown', this._onKeydown);
		}

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('ObservationList-CSS', _CSS, 520);
		}
	}

	get cfg() { return this.options || {}; }
	get providerName() { return this.cfg.ProviderIdentifier || 'ObservationList'; }
	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	get customerID() { return (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0; }
	get L() { return (typeof window !== 'undefined' && window.L) ? window.L : null; }  // Leaflet — loaded globally in index.html

	_get(pURL) { return new Promise((resolve) => this.restClient.getJSON(pURL, (e, b) => resolve(e ? null : b))); }
	_post(pURL, pBody) { return new Promise((resolve) => this.restClient.postJSON(pURL, pBody, (e, b) => resolve(e ? null : b))); }
	_esc(pV) { return String(pV == null ? '' : pV).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
	_mediaUrl(pPath) { const rc = this.restClient; if (rc && (typeof rc.formatUrl === 'function')) { try { return rc.formatUrl(pPath); } catch (e) { /* */ } } return `/1.0/${pPath}`; }
	_fmtDate(pV) { if (!pV) { return ''; } try { const d = new Date(pV); if (!Number.isNaN(d.getTime())) { return d.toLocaleDateString(); } } catch (e) { /* */ } return String(pV).slice(0, 10); }
	_idOf(pDoc) { return pDoc.IDRecord || (pDoc.id ? Number(String(pDoc.id).replace(/^Observation_/, '')) : 0); }

	_fmt(pN) { return Math.round(Number(pN) || 0).toLocaleString(); }
	// Readable fallback label when the catalog DisplayName isn't available (e.g. slow ObservationManifests): split
	// CamelCase / acronyms into words — "LADOTDPersonnel" → "LADOTD Personnel", "SiteManagerWeather" → "Site Manager Weather".
	_prettyType(pS) { return String(pS || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/_/g, ' ').replace(/\s+/g, ' ').trim(); }
	_compact(pN) { const n = Number(pN) || 0; if (n >= 1e6) { return `${(n / 1e6).toFixed(1)}M`; } if (n >= 1e3) { return `${Math.round(n / 1e3)}K`; } return String(n); }
	_facetPairs(pRes, pField) { const ff = pRes && pRes.facet_counts && pRes.facet_counts.facet_fields && pRes.facet_counts.facet_fields[pField]; const out = []; if (Array.isArray(ff)) { for (let i = 0; i < ff.length - 1; i += 2) { out.push([ ff[i], Number(ff[i + 1]) || 0 ]); } } return out; }

	// --- data ---
	// pExclude omits one dimension so a facet panel still shows the other values you could switch to (drill-down faceting).
	_buildBody(pExclude)
	{
		const f = this._filter; const b = {};
		if (f.IDProject) { b.IDProject = Number(f.IDProject); }
		if (f.SearchText && f.SearchText.trim()) { b.SearchText = f.SearchText.trim(); }
		if (f.Types && f.Types.length && pExclude !== 'type') { b.ObservationType = f.Types.slice(); }
		if (f.Authors && f.Authors.length) { b.IDAuthor = f.Authors.map(Number); }
		if (f.From && f.To) { b.DesignatedTime = { From: new Date(f.From).toISOString(), To: new Date(`${f.To}T23:59:59`).toISOString() }; }
		if (pExclude !== 'reporting')
		{
			if (f.Inclusion === '0') { b.DocumentCount = { Compare: '=', Value: 0 }; }
			else if (f.Inclusion === '1') { b.DocumentCount = { Compare: '=', Value: 1 }; }
			else if (f.Inclusion === '2') { b.DocumentCount = { Compare: '>=', Value: 2 }; }
		}
		// Labels — { TagType: [values] } → Solr <TagType>_Labelss:("v"). Only include non-empty types.
		const tmpLabels = {};
		Object.keys(f.Labels || {}).forEach((pType) => { const arr = (f.Labels[pType] || []).filter(Boolean); if (arr.length) { tmpLabels[pType] = arr; } });
		if (Object.keys(tmpLabels).length) { b.Labels = tmpLabels; if (f.MatchAllLabels) { b.MatchAllTags = true; } }
		return b;
	}

	async loadAsync()
	{
		this._loading = true; this._error = ''; this._loaded = true;
		this.render();
		try
		{
			await this._ensureTypeOptions();
			if (this._viewMode === 'charts') { await this._loadChartsAsync(); }
			else if (this._viewMode === 'map') { await this._loadMapAsync(); }
			else { await this._loadListAsync(); }
		}
		catch (pError) { this._error = (pError && pError.message) || String(pError); this._rows = []; this._total = 0; }
		this._loading = false;
		this.render();
	}

	async _loadListAsync()
	{
		const tmpBody = this._buildBody();
		const tmpResult = await this._post(`ObservationsFilter/Index/${this._page * this._cap}/${this._cap}`, tmpBody);
		if (!tmpResult) { throw new Error('The observation search did not respond.'); }
		this._rows = Array.isArray(tmpResult.docs) ? tmpResult.docs : [];
		this._total = (typeof tmpResult.numFound === 'number') ? tmpResult.numFound : this._rows.length;
		await this._resolveAuthors();
	}

	// Charts view — REAL server-side drill-down faceting: one facet POST per panel (excluding its own dimension so it
	// still offers the other values you could switch to) + one for the filtered total, all in parallel; the badge shows
	// the true round-trip. Clicking a bar sets the SAME shared filter the list and map use.
	async _loadChartsAsync()
	{
		const tmpT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		const [ tmpTypeRes, tmpRepRes, tmpTotalRes ] = await Promise.all([
			this._post('ObservationsFilter/Index/0/0', Object.assign(this._buildBody('type'), { Facets: [ 'RecordSubType' ] })),
			this._post('ObservationsFilter/Index/0/0', Object.assign(this._buildBody('reporting'), { Facets: [ 'DocumentCount' ] })),
			this._post('ObservationsFilter/Index/0/0', this._buildBody()),
		]);
		const tmpT1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
		this._chartLatencyMs = Math.max(1, Math.round(tmpT1 - tmpT0));

		this._chartTypeCounts = {};
		this._facetPairs(tmpTypeRes, 'RecordSubType').forEach(([ rst, cnt ]) => { if (rst) { this._chartTypeCounts[rst] = cnt; } });

		const tmpRep = { b0: 0, b1: 0, b2plus: 0 };
		this._facetPairs(tmpRepRes, 'DocumentCount').forEach(([ v, cnt ]) => { const n = Number(v); if (n <= 0) { tmpRep.b0 += cnt; } else if (n === 1) { tmpRep.b1 += cnt; } else { tmpRep.b2plus += cnt; } });
		this._rep = tmpRep;

		this._total = (tmpTotalRes && typeof tmpTotalRes.numFound === 'number') ? tmpTotalRes.numFound : 0;

		if (!this._typeChartOrder && this._typeOptions)
		{
			this._typeChartOrder = this._typeOptions
				.map((g) => ({ label: g.Label, c: g.Names.reduce((a, n) => a + (this._chartTypeCounts[n] || 0), 0) }))
				.sort((a, b) => (b.c - a.c)).map((x) => x.label);
		}
		this._chartsLoaded = true;
	}

	// Map view — the SAME search index the list/charts use (the docs carry Capture/User lat/long directly, so no per-row
	// hydration is needed). We fetch the most-recent _MAP_FETCH matches of the shared filter and plot those with a real
	// coordinate; DateOfRecord-desc means "the most recent N" when the match set is larger than the cap.
	async _loadMapAsync()
	{
		const tmpBody = this._buildBody();
		const tmpResult = await this._post(`ObservationsFilter/Index/0/${_MAP_FETCH}`, tmpBody);
		if (!tmpResult) { throw new Error('The observation search did not respond.'); }
		const tmpDocs = Array.isArray(tmpResult.docs) ? tmpResult.docs : [];
		this._total = (typeof tmpResult.numFound === 'number') ? tmpResult.numFound : tmpDocs.length;
		this._rows = tmpDocs;   // reuse the list plumbing (author resolution) — not rendered as a table in map mode
		this._mapFetched = tmpDocs.length;
		this._mapRows = tmpDocs.map((d) => ({ doc: d, id: this._idOf(d), pt: this._pointOf(d) }));
		this._mapMappable = this._mapRows.filter((r) => r.pt);
		await this._resolveAuthors();
		// If the open detail is no longer in the filtered/mapped set, close it (its marker is gone).
		if (this._mapDetail && !this._mapMappable.some((r) => r.id === this._mapDetail)) { this._mapDetail = 0; }
		this._mapLoaded = true;
		this._mapFitPending = true;   // a fresh data set → the next mount should fit the map to it
	}

	// Capture (device / EXIF) coordinate, falling back to the user coordinate; 0/near-0 is the "no location" sentinel.
	_pointOf(pDoc)
	{
		const f = (pLat, pLng) =>
		{
			const lat = parseFloat(pLat); const lng = parseFloat(pLng);
			if (!Number.isFinite(lat) || !Number.isFinite(lng)) { return null; }
			if ((Math.abs(lat) < 0.0000001) && (Math.abs(lng) < 0.0000001)) { return null; }
			if ((Math.abs(lat) > 90) || (Math.abs(lng) > 180)) { return null; }
			return { lat: lat, lng: lng };
		};
		return f(pDoc.CaptureLatitude, pDoc.CaptureLongitude) || f(pDoc.UserLatitude, pDoc.UserLongitude);
	}

	// A stable color per observation-type group, matching the Charts palette (position in _typeOptions → _CAT), so the
	// map, the type dropdown counts, and the charts all read the same. Rebuilt whenever _typeOptions is re-derived.
	_typeColorMap()
	{
		if (this._typeColorByRST && this._typeColorForOptions === this._typeOptions) { return this._typeColorByRST; }
		const tmpMap = {};
		(this._typeOptions || []).forEach((g, i) => { const c = _CAT[i % _CAT.length]; g.Names.forEach((n) => { tmpMap[n] = c; }); });
		this._typeColorByRST = tmpMap; this._typeColorForOptions = this._typeOptions;
		return tmpMap;
	}
	_typeColorFor(pRST) { return this._typeColorMap()[pRST] || '#8894a4'; }
	_typeGroupLabelFor(pRST) { const g = (this._typeOptions || []).find((x) => x.Names.indexOf(pRST) >= 0); return g ? g.Label : this._prettyType(pRST); }

	async _ensureTypeOptions()
	{
		// Cache only once the catalog actually grouped the types to their DisplayNames. If the ObservationManifests
		// catalog is slow or fails, we fall back to raw RecordSubType names for THIS load but retry on the next one
		// (so a transient catalog hiccup never sticks the session on ugly names like "LADOTDPersonnel").
		if (this._typeOptions && this._typeGrouped) { return; }
		this._typeChartOrder = null;  // rebuilding the options → recompute the stable chart order
		// PREFERRED: the opt-in Solr `RecordSubType` facet returns the observation types actually PRESENT in the customer's
		// indexed data, with counts (scoped server-side to IDCustomer + Deleted:0). It needs the `Facets` pass-through on
		// ObservationsFilter/Index; on an API without it the facet call returns no facet_counts and we fall back to the
		// customer's configured manifest types (ProjectObservationManifestJoin). Either way one chip = one catalog
		// DisplayName (so "Weather" covers "Legacy Weather" + "SiteManagerWeather"). The underlying values sent as the
		// ObservationType filter are RecordSubTypes (facet path) or manifest Names (fallback) — the server strips a leading
		// "Legacy " on both, so both match the indexed RecordSubType identically.
		// No server sort (FSF) — it scans/sorts the manifest table and is the slow/flaky part; we sort by DisplayName client-side.
		const tmpCatalogP = this._get('ObservationManifests/FilteredTo/FBV~Deleted~EQ~0/0/300');
		const tmpFacet = await this._post('ObservationsFilter/Index/0/0', { Facets: [ 'RecordSubType' ] });
		const tmpPairs = tmpFacet && tmpFacet.facet_counts && tmpFacet.facet_counts.facet_fields && tmpFacet.facet_counts.facet_fields.RecordSubType;
		if (tmpFacet && typeof tmpFacet.numFound === 'number') { this._baseline = tmpFacet.numFound; }  // unfiltered total (KPI context)
		const tmpCatalog = await tmpCatalogP;
		const tmpCatalogOk = Array.isArray(tmpCatalog) && tmpCatalog.length > 0;

		if (Array.isArray(tmpPairs) && tmpPairs.length)
		{
			// RecordSubType == manifest Name minus a leading "Legacy " — map each to its catalog DisplayName for the chip.
			const tmpDisplayByRST = {};
			(Array.isArray(tmpCatalog) ? tmpCatalog : []).forEach((m) => { if (m.Name) { const rst = String(m.Name).replace(/^Legacy /, ''); if (!tmpDisplayByRST[rst]) { tmpDisplayByRST[rst] = m.DisplayName || m.Name; } } });
			// facet_fields is a flat [ value, count, value, count, ... ] array.
			const tmpByLabel = {};
			for (let i = 0; i < tmpPairs.length - 1; i += 2)
			{
				const tmpVal = tmpPairs[i]; const tmpCount = Number(tmpPairs[i + 1]) || 0;
				if (!tmpVal) { continue; }
				const tmpLabel = tmpDisplayByRST[tmpVal] ? String(tmpDisplayByRST[tmpVal]).trim() : this._prettyType(tmpVal);
				const tmpGroup = (tmpByLabel[tmpLabel] = tmpByLabel[tmpLabel] || { Label: tmpLabel, Names: [], Count: 0 });
				if (tmpGroup.Names.indexOf(tmpVal) < 0) { tmpGroup.Names.push(tmpVal); }
				tmpGroup.Count += tmpCount;
			}
			this._typeOptions = Object.keys(tmpByLabel).sort((a, b) => a.localeCompare(b)).map((k) => tmpByLabel[k]);
			if (this._typeOptions.length) { this._typeGrouped = tmpCatalogOk; return; }
		}

		// FALLBACK (API without the `Facets` pass-through): the customer's configured manifest types, grouped by DisplayName.
		const tmpJoins = await this._get(`ProjectObservationManifestJoins/FilteredTo/FBV~IDCustomer~EQ~${this.customerID}/0/5000`);
		const tmpDisplayByName = {};
		(Array.isArray(tmpCatalog) ? tmpCatalog : []).forEach((m) => { if (m.Name) { tmpDisplayByName[m.Name] = m.DisplayName || m.Name; } });
		const tmpUsedNames = [ ...new Set((Array.isArray(tmpJoins) ? tmpJoins : []).map((r) => r.ObservationManifestName).filter(Boolean)) ];
		const fGroup = (pNames) =>
		{
			const tmpByLabel = {};
			pNames.forEach((n) => { const tmpLabel = String(tmpDisplayByName[n] || n).trim(); (tmpByLabel[tmpLabel] = tmpByLabel[tmpLabel] || []).push(n); });
			return Object.keys(tmpByLabel).sort((a, b) => a.localeCompare(b)).map((pLabel) => ({ Label: pLabel, Names: [ ...new Set(tmpByLabel[pLabel]) ] }));
		};
		// Fall back to the full catalog only if the customer has no configured manifests (better than an empty type filter).
		this._typeOptions = tmpUsedNames.length ? fGroup(tmpUsedNames) : fGroup(Object.keys(tmpDisplayByName));
		this._typeGrouped = tmpCatalogOk;  // retry next load if the catalog wasn't available
	}

	async _resolveAuthors()
	{
		const tmpIDs = [ ...new Set(this._rows.map((d) => d.IDAuthor).filter((x) => x && !this._authorNames[x])) ];
		if (!tmpIDs.length) { return; }
		const tmpUsers = await this._get(`Users/FilteredTo/FBL~IDUser~INN~${tmpIDs.join(',')}/0/${tmpIDs.length}`);
		(Array.isArray(tmpUsers) ? tmpUsers : []).forEach((u) => { this._authorNames[u.IDUser] = [ u.NameFirst, u.NameLast ].filter(Boolean).join(' ') || u.LoginID || `#${u.IDUser}`; });
	}

	// --- interactions ---
	setSearch(pV) { this._filter.SearchText = String(pV == null ? '' : pV); }
	setDateFrom(pV) { this._filter.From = pV || ''; }
	setDateTo(pV) { this._filter.To = pV || ''; }
	setInclusion(pV) { this._filter.Inclusion = pV || ''; this.applyFilters(); }
	toggleType(pIndex)
	{
		const tmpOpt = (this._typeOptions || [])[Number(pIndex)];
		if (!tmpOpt) { return; }
		const tmpOn = tmpOpt.Names.every((n) => this._filter.Types.indexOf(n) >= 0);
		if (tmpOn) { this._filter.Types = this._filter.Types.filter((n) => tmpOpt.Names.indexOf(n) < 0); }
		else { tmpOpt.Names.forEach((n) => { if (this._filter.Types.indexOf(n) < 0) { this._filter.Types.push(n); } }); }
		this.applyFilters();
	}
	toggleTypeDropdown() { this._typeDropdownOpen = !this._typeDropdownOpen; this.render(); }
	clearTypes() { if (!this._filter.Types.length) { return; } this._filter.Types = []; this.applyFilters(); }
	removeAuthor(pID)
	{
		const tmpID = Number(pID);
		this._filter.Authors = this._filter.Authors.filter((x) => Number(x) !== tmpID);
		this.applyFilters();
	}
	addAuthor(pID, pName)
	{
		const tmpID = Number(pID); if (!tmpID) { return; }
		if (this._filter.Authors.map(Number).indexOf(tmpID) < 0) { this._filter.Authors.push(tmpID); if (pName) { this._authorNames[tmpID] = pName; } }
		this.applyFilters();
	}
	selectProject(pID, pName)
	{
		this._filter.IDProject = Number(pID) || 0; this._filter.ProjectName = pName || '';
		this._filter.Labels = {}; this._activeTagType = ''; this._projectTagTypes = null;
		if (this._filter.IDProject) { this._loadProjectTagTypes(this._filter.IDProject); }
		this.applyFilters();
	}
	clearFilters()
	{
		// In an embed the project is locked, so Clear keeps it (and its labels vocabulary) and only resets the rest.
		const tmpProj = this._embeddedProjectID || 0;
		this._filter = { SearchText: '', Types: [], Authors: [], AuthorNames: {}, From: '', To: '', Inclusion: '', IDProject: tmpProj, ProjectName: this._filter.ProjectName || '', Labels: {}, MatchAllLabels: false };
		if (!tmpProj) { this._projectTagTypes = null; this._activeTagType = ''; }
		this.applyFilters();
	}

	// --- embed API (called by the Project workspace Observations tab): lock to a project, hide the Project filter,
	// paint into the tab host. Idempotent (the workspace re-mounts on every tab paint). ---
	openInto(pIDProject, pHostSelector)
	{
		const tmpID = Number(pIDProject) || 0;
		const tmpHost = pHostSelector || this._mount;
		const tmpChanged = (this._embeddedProjectID !== tmpID) || (this._mount !== tmpHost);
		this._embeddedProjectID = tmpID;
		this._mount = tmpHost;
		if (this._filter.IDProject !== tmpID)
		{
			this._filter.IDProject = tmpID; this._filter.ProjectName = '';
			this._filter.Labels = {}; this._activeTagType = ''; this._projectTagTypes = null;
			if (tmpID) { this._loadProjectTagTypes(tmpID); }
		}
		if (tmpChanged || !this._loaded)
		{
			try { if (this.pict.CSSMap && (typeof this.pict.CSSMap.injectCSS === 'function')) { this.pict.CSSMap.injectCSS(); } } catch (pError) { /* */ }
			this._page = 0; this.loadAsync();
		}
		else { this.render(); }  // plain repaint (the host div was re-created) — no refetch
	}
	// Return to the standalone screen: unlock the project and paint back into the standalone host.
	exitEmbed()
	{
		if (!this._embeddedProjectID && this._mount === '#ObservationList-Host') { return; }
		this._embeddedProjectID = 0;
		this._mount = '#ObservationList-Host';
		this._filter.IDProject = 0; this._filter.ProjectName = '';
		this._filter.Labels = {}; this._activeTagType = ''; this._projectTagTypes = null;
	}

	/** Load the selected project's tag-type vocabulary (project types, falling back to customer, then platform defaults). */
	async _loadProjectTagTypes(pID)
	{
		const fTypes = (rows) => [ ...new Set((Array.isArray(rows) ? rows : []).map((r) => r.TagType).filter(Boolean)) ];
		let tmpList = fTypes(await this._get(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~${pID}~FBV~IDCustomer~EQ~${this.customerID}~FSF~TagType~ASC~0/0/1000`));
		if (!tmpList.length) { tmpList = fTypes(await this._get(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~${this.customerID}~FSF~TagType~ASC~0/0/1000`)); }
		if (!tmpList.length) { tmpList = fTypes(await this._get('ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~0~FSF~TagType~ASC~0/0/1000')); }
		this._projectTagTypes = tmpList.sort((a, b) => a.localeCompare(b));
		this._activeTagType = this._projectTagTypes[0] || '';
		this.render();
	}

	setActiveTagType(pType) { this._activeTagType = pType || ''; }
	addLabel(pType, pValue) { const t = pType || this._activeTagType; if (!t || !pValue) { return; } this._filter.Labels[t] = this._filter.Labels[t] || []; if (this._filter.Labels[t].indexOf(pValue) < 0) { this._filter.Labels[t].push(pValue); } this.applyFilters(); }
	removeLabel(pType, pValue) { if (this._filter.Labels[pType]) { this._filter.Labels[pType] = this._filter.Labels[pType].filter((v) => v !== pValue); if (!this._filter.Labels[pType].length) { delete this._filter.Labels[pType]; } } this.applyFilters(); }
	toggleMatchAllLabels() { this._filter.MatchAllLabels = !this._filter.MatchAllLabels; if (Object.keys(this._filter.Labels).length) { this.applyFilters(); } else { this.render(); } }
	applyFilters() { this._page = 0; this.loadAsync(); }
	setPage(pPage) { const tmpMax = Math.max(0, Math.ceil(this._total / this._cap) - 1); this._page = Math.min(Math.max(0, pPage), tmpMax); this.loadAsync(); }
	openObservation(pID) { const tmpID = Number(pID) || 0; if (tmpID) { try { window.location.hash = `#/Observation/Workspace/${tmpID}`; } catch (e) { /* */ } } }

	// --- view mode + charts view ---
	setViewMode(pMode)
	{
		if (pMode !== 'list' && pMode !== 'charts' && pMode !== 'map') { return; }
		if (this._viewMode === pMode) { return; }
		this._viewMode = pMode;
		this.loadAsync();
	}
	// Full screen: overlay the dashboard across the whole browser view (above the app nav). State is a provider field so it
	// survives the shell rebuild; render() re-applies the `.obl-fs` class. Navigating away replaces the host, so the fixed
	// overlay is torn down with it (no leak).
	toggleFullscreen()
	{
		if (this._embeddedProjectID) { return; }
		this._fullscreen = !this._fullscreen;
		this.render();
		// The overlay composites on its own layer (CSS `transform` on .obl-fs) so it paints immediately without the classic
		// "position:fixed stays blank until you scroll" quirk. On the map, re-fit Leaflet to the new size next frame.
		if (this._viewMode === 'map' && typeof requestAnimationFrame === 'function')
		{
			requestAnimationFrame(() => { if (this._map) { try { this._map.invalidateSize(); } catch (pError) { /* */ } } });
		}
	}
	// Reporting bucket -> the shared "In reports" (Inclusion) filter, so it drives the list and map too.
	setReportingBucket(pBucket)
	{
		const tmpInc = { b0: '0', b1: '1', b2plus: '2' }[pBucket] || '';
		this._filter.Inclusion = (this._filter.Inclusion === tmpInc) ? '' : tmpInc;
		this.applyFilters();
	}

	_chartGroups()
	{
		const tmpOrder = this._typeChartOrder || (this._typeOptions || []).map((g) => g.Label);
		const tmpByLabel = {};
		(this._typeOptions || []).forEach((g, i) => { tmpByLabel[g.Label] = { label: g.Label, names: g.Names, idx: i, count: g.Names.reduce((a, n) => a + (this._chartTypeCounts[n] || 0), 0) }; });
		return tmpOrder.map((l) => tmpByLabel[l]).filter(Boolean);
	}
	_chartKPIs()
	{
		const tmpRepTotal = this._rep.b0 + this._rep.b1 + this._rep.b2plus;
		const inc = this._filter.Inclusion;
		let awaiting; let inPct;
		if (inc === '0') { awaiting = this._total; inPct = 0; }
		else if (inc === '1' || inc === '2') { awaiting = 0; inPct = 100; }
		else { awaiting = this._rep.b0; inPct = tmpRepTotal ? Math.round(((this._rep.b1 + this._rep.b2plus) / tmpRepTotal) * 100) : 0; }
		return { total: this._total, types: this._chartGroups().filter((g) => g.count > 0).length, inPct: inPct, awaiting: awaiting };
	}

	_chartsHTML()
	{
		if (this._error) { return `<div class="obl-empty obl-error">${this._esc(this._error)}</div>`; }
		if (this._loading && !this._chartsLoaded) { return `<div class="obl-empty"><span class="obl-spin"></span> Faceting…</div>`; }
		const k = this._chartKPIs();
		return /*html*/`
			<div class="oxp">
				<div class="oxp-badge-row"><span class="oxp-badge"><span class="oxp-pulse${this._loading ? ' oxp-pulse-busy' : ''}"></span> re-faceted in <b id="oxp-badge-ms">${this._loading ? '…' : this._chartLatencyMs}</b> ms over <b>${this._compact(this._baseline)}</b> rows</span></div>
				${this._kpisChartHTML(k)}
				<div class="oxp-grid">${this._typeChartPanelHTML()}${this._reportChartPanelHTML()}</div>
				<div class="oxp-foot"><i class="fas fa-bolt"></i> Live — faceted on the search index per selection; clicking a bar sets the same filter the List and Map share. Project · author · label facets and a time trend are next.</div>
			</div>`;
	}
	_kpisChartHTML(k)
	{
		const tmpShare = this._baseline ? Math.round((k.total / this._baseline) * 100) : 100;
		const cell = (pEyebrow, pId, pVal, pLabel, pCls, pSub) => `<div class="oxp-kpi${pCls || ''}"><div class="oxp-eyebrow">${pEyebrow}</div><div class="oxp-v" data-kpi="${pId}" data-to="${pVal}">${this._prevKPI ? this._fmt(this._prevKPI[pId] != null ? this._prevKPI[pId] : pVal) : '0'}</div><div class="oxp-l">${pLabel}${pSub ? ` <span class="oxp-l-sub">${pSub}</span>` : ''}</div></div>`;
		return `<div class="oxp-kpis">${cell('Metric', 'total', k.total, 'Observations in view', '', `· ${tmpShare}% of ${this._compact(this._baseline)}`)}${cell('Metric', 'types', k.types, 'Observation types', '')}${cell('Metric', 'inPct', k.inPct, 'In a report', '', '%')}${cell('Attention', 'awaiting', k.awaiting, 'Awaiting a report', ' oxp-kpi-warn')}</div>`;
	}
	_typeChartPanelHTML()
	{
		const P = this.providerName; const tmpGroups = this._chartGroups();
		const tmpMax = tmpGroups.reduce((a, g) => Math.max(a, g.count), 1);
		const tmpRows = tmpGroups.map((g) =>
		{
			const on = g.names.length && g.names.every((n) => this._filter.Types.indexOf(n) >= 0);
			const w = Math.max(1.5, (g.count / tmpMax) * 100);
			const color = _CAT[g.idx % _CAT.length];
			return `<div class="oxp-row${on ? ' oxp-row-on' : ''}" onclick="_Pict.providers.${P}.toggleType(${g.idx})">
				<div class="oxp-row-bg" style="width:${w}%;background:linear-gradient(90deg,${color}22,transparent)"></div>
				<div class="oxp-lab"><span class="oxp-dot" style="background:${color}"></span><span class="oxp-name" title="${this._esc(g.label)}">${this._esc(g.label)}</span></div>
				<span class="oxp-cnt">${this._fmt(g.count)}</span>
				<div class="oxp-track"><div class="oxp-fill" style="width:${w}%;background:${color}"></div></div>
			</div>`;
		}).join('');
		return `<div class="oxp-panel"><div class="oxp-ph"><h3><i class="fas fa-chart-simple"></i> Observation type</h3><span class="oxp-hint">facet · RecordSubType — click to filter</span></div><div class="oxp-rows">${tmpRows || '<div class="oxp-empty">No types in view.</div>'}</div></div>`;
	}
	_reportChartPanelHTML()
	{
		const P = this.providerName; const sel = this._filter.Inclusion;
		const segs = [
			{ key: 'b0', inc: '0', name: 'Not in a report', count: this._rep.b0, color: _WARN },
			{ key: 'b1', inc: '1', name: 'In one report', count: this._rep.b1, color: _CAT[0] },
			{ key: 'b2plus', inc: '2', name: 'In two or more', count: this._rep.b2plus, color: _CAT[1] },
		];
		const sum = segs.reduce((a, s) => a + s.count, 0) || 1;
		const stack = segs.map((s) => `<div class="oxp-seg${sel && sel !== s.inc ? ' oxp-seg-dim' : ''}" style="width:${(s.count / sum) * 100}%;background:${s.color}" title="${this._esc(s.name)} · ${this._fmt(s.count)}" onclick="_Pict.providers.${P}.setReportingBucket('${s.key}')"></div>`).join('');
		const legend = segs.map((s) => `<div class="oxp-fl${sel === s.inc ? ' oxp-fl-on' : ''}" onclick="_Pict.providers.${P}.setReportingBucket('${s.key}')"><span class="oxp-sw" style="background:${s.color}"></span><span class="oxp-fname">${this._esc(s.name)}</span><span class="oxp-fpct">${((s.count / sum) * 100).toFixed(1)}%</span><span class="oxp-fcnt">${this._fmt(s.count)}</span></div>`).join('');
		const awaitPct = sum ? ((this._rep.b0 / sum) * 100) : 0;
		return `<div class="oxp-panel"><div class="oxp-ph"><h3><i class="fas fa-clipboard-check"></i> Reporting status</h3><span class="oxp-hint">facet · DocumentCount</span></div><div class="oxp-stack">${stack}</div><div class="oxp-flegend">${legend}</div>${this._rep.b0 > 0 ? `<div class="oxp-callout"><i class="fas fa-triangle-exclamation"></i><p><b>${this._fmt(this._rep.b0)}</b> — ${awaitPct.toFixed(1)}% of the current view — aren't in any report yet. <a href="#" onclick="_Pict.providers.${P}.setReportingBucket('b0');return false;">Isolate them &rarr;</a></p></div>` : ''}</div>`;
	}
	// =========================================================== MAP ===========================================================
	_mapHTML()
	{
		if (this._error) { return `<div class="obl-empty obl-error">${this._esc(this._error)}</div>`; }
		if (this._loading && !this._mapLoaded) { return `<div class="obl-empty"><span class="obl-spin"></span> Mapping observations…</div>`; }
		if (!this.L) { return `<div class="obl-empty">The map library didn't load — reload the page to see the map.</div>`; }
		return `<div class="obl-maprow"><div id="obl-map" class="obl-map"></div><div id="obl-map-side" class="obl-side">${this._mapSideInnerHTML()}</div></div>`;
	}
	// (Re)build the Leaflet map into #obl-map. render() replaces the shell's innerHTML on every paint, so the map DOM is
	// always fresh here — we preserve the user's current center/zoom across BENIGN repaints (e.g. closing the type dropdown)
	// and only re-fit the bounds when the plotted dataset actually changes (a filter change). Marker clicks NEVER repaint.
	_mountObservationMap()
	{
		const L = this.L; const ca = this.pict.ContentAssignment;
		if (!L || !ca) { return; }
		const tmpHostList = ca.getElement('#obl-map');
		if (!tmpHostList || !tmpHostList.length)
		{
			if (this._map) { try { this._map.remove(); } catch (pError) { /* */ } this._map = null; }   // left map view — drop the instance
			return;
		}
		const tmpHost = tmpHostList[0];
		const tmpPrevView = this._map ? { center: this._map.getCenter(), zoom: this._map.getZoom() } : this._mapView;
		if (this._map) { try { this._map.remove(); } catch (pError) { /* */ } this._map = null; }
		this._map = L.map(tmpHost, { scrollWheelZoom: true }).setView([ 39.5, -98.35 ], 4);
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors' }).addTo(this._map);
		this._mapMarkers = [];
		this._mapMappable.forEach((r) =>
		{
			const tmpBase = this._typeColorFor(r.doc.RecordSubType);
			const m = L.circleMarker([ r.pt.lat, r.pt.lng ], { radius: 5, weight: 1, color: '#ffffff', fillColor: tmpBase, fillOpacity: 0.9 });
			m.bindTooltip(`${this._esc(this._typeGroupLabelFor(r.doc.RecordSubType))} · ${this._esc(this._fmtDate(r.doc.DateOfRecord))}`);
			m.on('click', () => { try { this.showObservationDetail(r.id); } catch (pError) { /* */ } });
			m.addTo(this._map); this._mapMarkers.push({ r: r, m: m, base: tmpBase });
		});
		this._restyleMapMarkers();
		const tmpPts = this._mapMarkers.map((x) => [ x.r.pt.lat, x.r.pt.lng ]);
		// Fit the bounds only when a data load is pending (entered the map, or the filter changed); a benign repaint
		// (e.g. closing the type dropdown) keeps the user's current center/zoom so the map never jumps under them.
		if (!this._mapFitPending && tmpPrevView) { try { this._map.setView(tmpPrevView.center, tmpPrevView.zoom); } catch (pError) { /* */ } }
		else if (tmpPts.length) { try { this._map.fitBounds(tmpPts, { padding: [ 28, 28 ], maxZoom: 15 }); } catch (pError) { /* */ } }
		this._mapFitPending = false;
		this._map.on('moveend zoomend', () => { if (this._map) { try { this._mapView = { center: this._map.getCenter(), zoom: this._map.getZoom() }; } catch (pError) { /* */ } } });
		setTimeout(() => { if (this._map) { try { this._map.invalidateSize(); } catch (pError) { /* */ } } }, 60);
	}
	// Emphasize the open observation's marker (amber ring, brought to front) so it's obvious which point the panel shows.
	_restyleMapMarkers()
	{
		if (!this._mapMarkers) { return; }
		const sel = this._mapDetail;
		this._mapMarkers.forEach((x) =>
		{
			if (sel && x.r.id === sel) { x.m.setStyle({ color: '#f59e0b', weight: 3, fillColor: x.base, fillOpacity: 1 }); x.m.setRadius(8); try { x.m.bringToFront(); } catch (pError) { /* */ } }
			else { x.m.setStyle({ color: '#ffffff', weight: 1, fillColor: x.base, fillOpacity: 0.9 }); x.m.setRadius(5); }
		});
	}
	// Marker click → show the observation in the docked side panel IN PLACE (update only #obl-map-side; no shell repaint,
	// so the map keeps its exact position and zoom as you click between points).
	showObservationDetail(pID) { const id = Number(pID) || 0; if (!id) { return; } this._mapDetail = id; this._restyleMapMarkers(); this._renderMapSide(); this._ensureMapDetailObsAsync(id); }
	closeObservationDetail() { this._mapDetail = 0; this._restyleMapMarkers(); this._renderMapSide(); }

	/** The centralized observation renderer (registered in PlatformConfiguration-Application-Web). */
	get _obsRender() { return this.pict && this.pict.HeadlightObservationRender; }

	/**
	 * Fetch the full observation (with its assembled .Details) + resolved schema for the docked side panel, so it can
	 * show the observation's CAPTURED DATA below the Solr metadata (a data-laden panel, via the centralized renderer).
	 * The list/map feed is Solr docs (metadata only), so the data is a lazy per-click fetch; guarded against stale loads.
	 */
	async _ensureMapDetailObsAsync(pID)
	{
		const id = Number(pID) || 0;
		if (!id || !this._obsRender) { return; }
		if (this._mapDetailObsID === id && this._mapDetailObs) { return; }
		this._mapDetailLoading = true;
		this._mapDetailObs = null; this._mapDetailObsID = id; this._mapDetailSpec = null;
		this._renderMapSide();
		const tmpObs = await this._get(`Observation/${id}`);
		if (this._mapDetail !== id) { return; }   // the user moved on — drop this result
		if (tmpObs && !tmpObs.Error && (typeof this._obsRender.loadSpecification === 'function') && tmpObs.ObservationType)
		{
			await new Promise((resolve) => this._obsRender.loadSpecification(tmpObs.ObservationType, tmpObs.IDProject,
				(pError, pSpec) => { if (this._mapDetail === id) { this._mapDetailSpec = pError ? null : pSpec; } resolve(); }));
		}
		if (this._mapDetail !== id) { return; }
		this._mapDetailObs = (tmpObs && !tmpObs.Error) ? tmpObs : null;
		this._mapDetailLoading = false;
		this._renderMapSide();
	}

	/** The captured-data block appended to the side detail panel (spinner while loading; the renderer's data view once ready). */
	_mapSideDataHTML()
	{
		if (this._mapDetailLoading) { return `<div class="obl-side-data"><div class="obl-side-data-h">Captured data</div><div class="obl-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div></div>`; }
		const tmpRender = this._obsRender;
		if (!tmpRender || !this._mapDetailObs || (this._mapDetailObsID !== this._mapDetail)) { return ''; }
		const tmpHTML = tmpRender.renderObservationHTML(this._mapDetailObs, { format: 'data', specification: this._mapDetailSpec || null });
		return `<div class="obl-side-data"><div class="obl-side-data-h">Captured data</div>${tmpHTML}</div>`;
	}
	_renderMapSide() { const ca = this.pict.ContentAssignment; if (!ca) { return; } const el = ca.getElement('#obl-map-side'); if (el && el.length) { el[0].innerHTML = this._mapSideInnerHTML(); } }
	_mapSideInnerHTML()
	{
		const tmpInfo = `<div class="obl-side-sum-wrap">${this._mapSummaryHTML()}</div>`;
		if (this._mapDetail) { return tmpInfo + this._mapSideDetailHTML(); }
		return tmpInfo + this._mapLegendHTML() + `<div class="obl-side-hint"><i class="fas fa-hand-pointer"></i> Click any point to read its observation here.</div>`;
	}
	_mapSummaryHTML()
	{
		const tmpMapped = this._mapMappable.length;
		const tmpNoLoc = this._mapFetched - tmpMapped;
		const tmpCapped = this._total > this._mapFetched;
		let tmpNote = '';
		if (tmpCapped) { tmpNote = ` <span class="obl-mut">· most recent ${this._mapFetched.toLocaleString()} of ${this._total.toLocaleString()} matches — refine filters to map the rest</span>`; }
		else if (tmpNoLoc > 0) { tmpNote = ` <span class="obl-mut">· ${tmpNoLoc.toLocaleString()} without a location</span>`; }
		return `<div class="obl-side-sum"><b>${tmpMapped.toLocaleString()}</b> observation${tmpMapped === 1 ? '' : 's'} mapped${tmpNote}</div>`;
	}
	// A legend of the observation-type groups present in the mapped set — colored to match the markers and clickable to
	// toggle that type in the SHARED filter (so the legend doubles as a quick type filter, like the charts panel).
	_mapLegendHTML()
	{
		const P = this.providerName;
		const tmpCounts = {};
		this._mapMappable.forEach((r) => { const lbl = this._typeGroupLabelFor(r.doc.RecordSubType); tmpCounts[lbl] = (tmpCounts[lbl] || 0) + 1; });
		const tmpRows = (this._typeOptions || [])
			.map((g, i) => ({ label: g.Label, idx: i, color: _CAT[i % _CAT.length], count: tmpCounts[g.Label] || 0, on: g.Names.length && g.Names.every((n) => this._filter.Types.indexOf(n) >= 0) }))
			.filter((x) => x.count > 0 || x.on)
			.sort((a, b) => (b.count - a.count));
		if (!tmpRows.length) { return ''; }
		const tmpItems = tmpRows.map((x) => `<div class="obl-legend-i${x.on ? ' obl-legend-on' : ''}" onclick="_Pict.providers.${P}.toggleType(${x.idx})" title="${this._esc(x.label)}"><span class="obl-legend-dot" style="background:${x.color}"></span><span class="obl-legend-v">${this._esc(x.label)}</span><span class="obl-legend-c">${x.count.toLocaleString()}</span></div>`).join('');
		return `<div class="obl-legend"><div class="obl-legend-h">Observation type <span class="obl-mut">click to filter</span></div><div class="obl-legend-list">${tmpItems}</div></div>`;
	}
	_mapSideDetailHTML()
	{
		const P = this.providerName; const id = this._mapDetail;
		const r = this._mapMappable.find((x) => x.id === id) || this._mapRows.find((x) => x.id === id);
		const d = r ? r.doc : null;
		if (!d) { return ''; }
		const tmpTitle = this._docTitle(d);
		const tmpAuthor = this._authorNames[d.IDAuthor] || (d.IDAuthor ? `#${d.IDAuthor}` : '');
		const tmpTypeLabel = this._typeGroupLabelFor(d.RecordSubType);
		const tmpColor = this._typeColorFor(d.RecordSubType);
		const tmpPt = (r && r.pt) ? r.pt : this._pointOf(d);
		// A small, crisp thumbnail beside the title (the /Image/Thumbnail size is the reliably-generated one; the larger
		// sizes return a placeholder until the transcoder builds them). Non-photo observation types show a typed glyph.
		const tmpThumb = d.IDArtifact
			? `<img class="obl-side-thumb" src="${this._esc(this._mediaUrl(`Observation/${id}/Image/Thumbnail`))}" alt="" loading="lazy" referrerpolicy="no-referrer" onerror="this.classList.add('obl-side-thumb-broken')">`
			: `<span class="obl-side-thumb obl-side-thumb-none" style="color:${tmpColor}"><i class="fas fa-clipboard"></i></span>`;
		const tmpProps = [
			this._sideProp('Type', `<span class="obl-prop-type"><span class="obl-legend-dot" style="background:${tmpColor}"></span>${this._esc(tmpTypeLabel)}</span>`),
			this._sideProp('Date', this._esc(this._fmtDate(d.DateOfRecord)) || '—'),
			this._sideProp('Author', this._esc(tmpAuthor) || '—'),
			this._sideProp('Project', this._esc(d.Project || '') || '—'),
			this._sideProp('In reports', this._reportBadge(d.DocumentCount)),
			tmpPt ? (this._sideProp('Latitude', tmpPt.lat.toFixed(5)) + this._sideProp('Longitude', tmpPt.lng.toFixed(5))) : '',
		].join('');
		return `<div class="obl-side-detail">
			<div class="obl-side-top">
				<button class="obl-side-x" onclick="_Pict.providers.${P}.closeObservationDetail()" title="Close"><i class="fas fa-xmark"></i></button>
				<div class="obl-side-idrow">
					${tmpThumb}
					<div class="obl-side-idtext">
						<span class="obl-side-name">${this._esc(tmpTitle)}</span>
						<div class="obl-side-sub"><span class="obl-type">${this._esc(d.RecordSubType || '')}</span><span class="obl-mut">#${id}</span></div>
					</div>
				</div>
			</div>
			<div class="obl-side-scroll"><div class="obl-props">${tmpProps}</div>${this._mapSideDataHTML()}</div>
			<div class="obl-side-foot"><a class="obl-side-btn" href="#/Observation/Workspace/${id}"><i class="fas fa-arrow-up-right-from-square"></i> Open observation</a></div>
		</div>`;
	}
	_sideProp(pLabel, pValueHtml) { return `<div class="obl-prop"><div class="obl-prop-l">${this._esc(pLabel)}</div><div class="obl-prop-v">${pValueHtml}</div></div>`; }
	_reportBadge(pCount) { const n = Number(pCount) || 0; if (n <= 0) { return '<span class="obl-rep obl-rep-0">Not in a report</span>'; } return `<span class="obl-rep obl-rep-1">In ${n} report${n === 1 ? '' : 's'}</span>`; }

	_bodyHTML()
	{
		if (this._viewMode === 'charts') { return this._chartsHTML(); }
		if (this._viewMode === 'map') { return this._mapHTML(); }
		return this._resultsHTML();
	}
	_viewTabsHTML()
	{
		const P = this.providerName;
		const tab = (pMode, pIcon, pLabel) => `<button class="obl-vtab${this._viewMode === pMode ? ' obl-vtab-on' : ''}" onclick="_Pict.providers.${P}.setViewMode('${pMode}')"><i class="fas ${pIcon}"></i> ${pLabel}</button>`;
		const tmpCount = this._loaded ? `<span class="obl-vtabcount">${this._total.toLocaleString()} match${this._total === 1 ? '' : 'es'}</span>` : '';
		// Full-screen toggle — standalone only (in an embedded Project tab there's no app chrome to escape).
		const tmpFsBtn = this._embeddedProjectID ? '' : `<button class="obl-fsbtn${this._fullscreen ? ' obl-fsbtn-on' : ''}" onclick="_Pict.providers.${P}.toggleFullscreen()" title="${this._fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}"><i class="fas ${this._fullscreen ? 'fa-compress' : 'fa-expand'}"></i> ${this._fullscreen ? 'Exit full screen' : 'Full screen'}</button>`;
		return `<div class="obl-viewtabs-row"><div class="obl-viewtabs">${tab('list', 'fa-list-ul', 'List')}${tab('charts', 'fa-chart-simple', 'Charts')}${tab('map', 'fa-location-dot', 'Map')}</div><div class="obl-vtabs-right">${tmpCount}${tmpFsBtn}</div></div>`;
	}
	_tweenChartKPIs()
	{
		const tmpCA = this.pict.ContentAssignment; if (!tmpCA) { return; }
		const els = tmpCA.getElement(`${this._mount} .oxp-v`);
		const reduced = (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches);
		const next = {};
		for (let i = 0; els && i < els.length; i++)
		{
			const el = els[i]; const key = el.getAttribute('data-kpi'); const to = Number(el.getAttribute('data-to')) || 0;
			const isPct = (key === 'inPct'); const from = this._prevKPI && this._prevKPI[key] != null ? this._prevKPI[key] : 0;
			next[key] = to;
			if (reduced || from === to) { el.textContent = isPct ? `${Math.round(to)}%` : this._fmt(to); continue; }
			this._animateKPI(el, from, to, isPct);
		}
		this._prevKPI = next;
	}
	_animateKPI(el, from, to, isPct)
	{
		const dur = 460; let t0 = null;
		const step = (ts) => { if (t0 === null) { t0 = ts; } const p = Math.min(1, (ts - t0) / dur); const e = 1 - Math.pow(1 - p, 3); const v = from + (to - from) * e; el.textContent = isPct ? `${Math.round(v)}%` : this._fmt(v); if (p < 1) { requestAnimationFrame(step); } };
		requestAnimationFrame(step);
	}

	// --- render (host-div paint) ---
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement(this._mount).length === 0) { return; }
		tmpCA.assignContent(this._mount, this._shellHTML());
		this._mountPickers();
		if (this._viewMode === 'charts') { this._tweenChartKPIs(); }
		if (this._viewMode === 'map') { this._mountObservationMap(); }
		else if (this._map) { try { this._map.remove(); } catch (pError) { /* */ } this._map = null; }   // left map view — tear down Leaflet
	}

	_shellHTML()
	{
		// Embedded in a Project workspace tab: the tab already says "Observations", so drop the redundant page title.
		return /*html*/`
			<div class="obl${this._embeddedProjectID ? ' obl-embedded' : ''}${this._fullscreen ? ' obl-fs' : ''}">
				${this._embeddedProjectID ? '' : `<div class="obl-head"><div class="obl-title"><i class="fas fa-clipboard-list"></i> Observations</div></div>`}
				${this._filterBarHTML()}
				${this._viewTabsHTML()}
				<div id="obl-results">${this._bodyHTML()}</div>
			</div>`;
	}

	_filterBarHTML()
	{
		const f = this._filter;
		const tmpAuthorChips = f.Authors.map((id) => `<span class="obl-tag">${this._esc(this._authorNames[id] || ('#' + id))}<button class="obl-tag-x" onclick="_Pict.providers.${this.providerName}.removeAuthor(${Number(id)})">&times;</button></span>`).join('');
		const P = this.providerName;
		return /*html*/`
			<div class="obl-filters">
				<div class="obl-row">
					<span class="obl-search"><i class="fas fa-search obl-search-ic"></i><input class="obl-search-in" type="search" placeholder="Search observations…" value="${this._esc(f.SearchText)}" oninput="_Pict.providers.${P}.setSearch(this.value)" onkeydown="if(event.key==='Enter'){_Pict.providers.${P}.applyFilters();}" /></span>
					<div class="obl-f"><span class="obl-f-l">Types</span>${this._typeDropdownHTML()}</div>
					<label class="obl-f"><span class="obl-f-l">From</span><input type="date" value="${this._esc(f.From)}" onchange="_Pict.providers.${P}.setDateFrom(this.value)" /></label>
					<label class="obl-f"><span class="obl-f-l">To</span><input type="date" value="${this._esc(f.To)}" onchange="_Pict.providers.${P}.setDateTo(this.value)" /></label>
					<div class="obl-f"><span class="obl-f-l">In reports</span><div id="ObservationList-InclusionPicker" class="obl-picker obl-enumpicker"></div></div>
					<button class="obl-apply" onclick="_Pict.providers.${P}.applyFilters()">Apply</button>
					<button class="obl-clear" onclick="_Pict.providers.${P}.clearFilters()">Clear</button>
				</div>
				<div class="obl-row obl-row-pickers">
					${this._embeddedProjectID ? '' : `<div class="obl-pick"><span class="obl-f-l">Project</span><div id="ObservationList-ProjectPicker" class="obl-picker"></div></div>`}
					<div class="obl-pick"><span class="obl-f-l">Author</span><div id="ObservationList-AuthorPicker" class="obl-picker"></div>${tmpAuthorChips ? `<div class="obl-tags">${tmpAuthorChips}</div>` : ''}</div>
				</div>
				${this._labelsFilterHTML()}
			</div>`;
	}

	/** Observation Types — a dropdown button + checklist panel (replaces the chip cluster). Multi-select; the panel
	 *  stays open across selections (open state is provider-held) and closes on an outside click or the button. */
	_typeDropdownHTML()
	{
		const P = this.providerName; const opts = this._typeOptions || [];
		const tmpSel = opts.filter((o) => o.Names.length && o.Names.every((n) => this._filter.Types.indexOf(n) >= 0)).length;
		const tmpBtn = `<button type="button" class="obl-ddbtn${this._typeDropdownOpen ? ' obl-ddbtn-open' : ''}" onclick="_Pict.providers.${P}.toggleTypeDropdown()"><span class="obl-ddbtn-l">${tmpSel ? `${tmpSel} selected` : 'Any type'}</span>${tmpSel ? `<span class="obl-ddbadge">${tmpSel}</span>` : ''}<i class="fas fa-chevron-${this._typeDropdownOpen ? 'up' : 'down'} obl-ddcaret"></i></button>`;
		if (!this._typeDropdownOpen) { return `<div class="obl-typedd">${tmpBtn}</div>`; }
		const tmpItems = opts.map((o, i) =>
		{
			const on = o.Names.length && o.Names.every((n) => this._filter.Types.indexOf(n) >= 0);
			const cnt = (o.Count != null) ? `<span class="obl-ddcount">${Number(o.Count).toLocaleString()}</span>` : '';
			return `<label class="obl-dditem${on ? ' obl-dditem-on' : ''}"><input type="checkbox" ${on ? 'checked' : ''} onclick="_Pict.providers.${P}.toggleType(${i})" /><span class="obl-ddname" title="${this._esc(o.Label)}">${this._esc(o.Label)}</span>${cnt}</label>`;
		}).join('');
		return `<div class="obl-typedd">${tmpBtn}<div class="obl-ddpanel"><div class="obl-ddpanel-head"><span>${opts.length} type${opts.length === 1 ? '' : 's'} in your data</span>${tmpSel ? `<button type="button" class="obl-ddclearbtn" onclick="_Pict.providers.${P}.clearTypes()">Clear</button>` : ''}</div><div class="obl-ddlist">${tmpItems || '<div class="obl-ddempty">No types</div>'}</div></div></div>`;
	}

	/** The project-scoped Labels filter: a tag-type selector + a searchable value picker, and the selected values as
	 *  removable chips grouped by type. Only shown once a project is selected (labels are a per-project vocabulary). */
	_labelsFilterHTML()
	{
		if (!this._filter.IDProject) { return `<div class="obl-labelhint"><i class="fas fa-tags"></i> Pick a project above to filter by its labels.</div>`; }
		if (!this._projectTagTypes) { return `<div class="obl-labelhint"><span class="obl-spin"></span> Loading labels…</div>`; }
		if (!this._projectTagTypes.length) { return ''; }
		const P = this.providerName;
		const tmpTypeSelect = `<select class="obl-labeltype" onchange="_Pict.providers.${P}.setActiveTagType(this.value)">${this._projectTagTypes.map((t) => `<option value="${this._esc(t)}"${t === this._activeTagType ? ' selected' : ''}>${this._esc(t)}</option>`).join('')}</select>`;
		const tmpChosen = Object.keys(this._filter.Labels).map((pType) =>
		{
			const vals = this._filter.Labels[pType] || []; if (!vals.length) { return ''; }
			return `<span class="obl-label-grp"><span class="obl-label-gt">${this._esc(pType)}</span>${vals.map((v) => `<span class="obl-tag">${this._esc(v)}<button class="obl-tag-x" onclick="_Pict.providers.${P}.removeLabel('${this._esc(pType)}','${this._esc(v)}')">&times;</button></span>`).join('')}</span>`;
		}).join('');
		const tmpHasLabels = Object.keys(this._filter.Labels).length > 0;
		return `<div class="obl-labels-filter">
			<span class="obl-f-l">Labels</span>
			${tmpTypeSelect}
			<div id="ObservationList-LabelPicker" class="obl-picker obl-labelpicker"></div>
			${tmpHasLabels ? `<label class="obl-matchall"><input type="checkbox"${this._filter.MatchAllLabels ? ' checked' : ''} onchange="_Pict.providers.${P}.toggleMatchAllLabels()" /> Match all</label>` : ''}
			${tmpChosen ? `<div class="obl-label-chips">${tmpChosen}</div>` : ''}
		</div>`;
	}

	_resultsHTML()
	{
		if (this._loading) { return `<div class="obl-empty"><span class="obl-spin"></span> Searching…</div>`; }
		if (this._error) { return `<div class="obl-empty obl-error">${this._esc(this._error)}</div>`; }
		if (!this._rows.length) { return `<div class="obl-empty">No observations match these filters.</div>`; }
		const tmpRows = this._rows.map((d) => this._rowHTML(d)).join('');
		return `<div class="obl-listwrap"><table class="obl-list"><thead><tr><th></th><th>Type</th><th>Observation</th><th>Date</th><th>Author</th><th>Project</th><th class="obl-num">Docs</th></tr></thead><tbody>${tmpRows}</tbody></table></div>${this._paginationHTML()}`;
	}

	_rowHTML(pDoc)
	{
		const tmpID = this._idOf(pDoc);
		const tmpThumb = pDoc.IDArtifact ? `<img class="obl-thumb" src="${this._mediaUrl(`Observation/${tmpID}/Image/Thumbnail`)}" loading="lazy" onerror="this.classList.add('obl-thumb-broken')"/>` : '<span class="obl-thumb obl-thumb-none"><i class="fas fa-clipboard"></i></span>';
		const tmpTitle = this._docTitle(pDoc);
		const tmpAuthor = this._authorNames[pDoc.IDAuthor] || (pDoc.IDAuthor ? `#${pDoc.IDAuthor}` : '');
		return `<tr class="obl-r" onclick="_Pict.providers.${this.providerName}.openObservation(${tmpID})">
			<td class="obl-thumb-cell">${tmpThumb}</td>
			<td><span class="obl-type">${this._esc(pDoc.RecordSubType || '')}</span></td>
			<td class="obl-obscell"><span class="obl-obsname">${this._esc(tmpTitle)}</span><span class="obl-obsid">#${tmpID}</span></td>
			<td class="obl-nowrap">${this._esc(this._fmtDate(pDoc.DateOfRecord))}</td>
			<td>${this._esc(tmpAuthor)}</td>
			<td class="obl-proj">${this._esc(pDoc.Project || '')}</td>
			<td class="obl-num">${pDoc.DocumentCount != null ? this._esc(pDoc.DocumentCount) : ''}</td>
		</tr>`;
	}

	/** A readable row title from the Solr doc — the search text is a concatenation; trim to a sensible label. */
	_docTitle(pDoc)
	{
		const tmpText = String(pDoc.SearchText || '').trim().replace(/\s+/g, ' ');
		if (tmpText) { return tmpText.length > 90 ? `${tmpText.slice(0, 90)}…` : tmpText; }
		return pDoc.RecordSubType || `Observation ${this._idOf(pDoc)}`;
	}

	_paginationHTML()
	{
		const tmpPages = Math.max(1, Math.ceil(this._total / this._cap));
		if (tmpPages <= 1) { return ''; }
		const P = this.providerName;
		const tmpStart = this._page * this._cap + 1;
		const tmpEnd = Math.min(this._total, (this._page + 1) * this._cap);
		return `<div class="obl-pager">
			<button class="obl-pg" ${this._page <= 0 ? 'disabled' : ''} onclick="_Pict.providers.${P}.setPage(${this._page - 1})"><i class="fas fa-chevron-left"></i> Prev</button>
			<span class="obl-pg-info">${tmpStart.toLocaleString()}–${tmpEnd.toLocaleString()} of ${this._total.toLocaleString()}</span>
			<button class="obl-pg" ${this._page >= (tmpPages - 1) ? 'disabled' : ''} onclick="_Pict.providers.${P}.setPage(${this._page + 1})">Next <i class="fas fa-chevron-right"></i></button>
		</div>`;
	}

	// --- pickers (project + author, searchable, via pict-section-picker) ---
	_mountPickers()
	{
		const tmpPicker = this.pict.providers && this.pict.providers['Pict-Section-Picker'];
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		if (tmpCA.getElement('#ObservationList-ProjectPicker').length > 0)
		{
			const tmpV = tmpPicker.createPicker(`${this.providerName}-ProjectPicker`,
				{
					DestinationAddress: '#ObservationList-ProjectPicker', ValueAddress: `AppData.${this.providerName}.PickerProject`,
					Searchable: true, AllowClear: true, ClearLabel: '— any project —', Placeholder: 'Any project…',
					DataProvider: this._projectDataProvider(), ResolveValue: this._projectResolveValue(),
					OnChange: (v, pRecord) => this.selectProject(v, pRecord && (pRecord.Text || pRecord.Name)),
				});
			if (tmpV && (typeof tmpV.render === 'function')) { tmpV.render(); }
			if (tmpV && (typeof tmpV.setValue === 'function')) { tmpV.setValue(this._filter.IDProject || ''); }
		}
		if (tmpCA.getElement('#ObservationList-LabelPicker').length > 0)
		{
			const tmpV = tmpPicker.createPicker(`${this.providerName}-LabelPicker`,
				{
					DestinationAddress: '#ObservationList-LabelPicker', ValueAddress: `AppData.${this.providerName}.PickerLabel`,
					Searchable: true, AllowClear: true, ClearLabel: '— add a label value —', Placeholder: 'Add a label value…',
					DataProvider: this._labelValueDataProvider(), ResolveValue: () => Promise.resolve(null),
					OnChange: (v) => { if (v) { this.addLabel(this._activeTagType, v); } },
				});
			if (tmpV && (typeof tmpV.render === 'function')) { tmpV.render(); }
		}
		if (tmpCA.getElement('#ObservationList-AuthorPicker').length > 0)
		{
			const tmpV = tmpPicker.createPicker(`${this.providerName}-AuthorPicker`,
				{
					DestinationAddress: '#ObservationList-AuthorPicker', ValueAddress: `AppData.${this.providerName}.PickerAuthor`,
					Searchable: true, AllowClear: true, ClearLabel: '— add author —', Placeholder: 'Add an author…',
					DataProvider: this._authorDataProvider(), ResolveValue: () => Promise.resolve(null),
					OnChange: (v, pRecord) => { if (v) { this.addAuthor(v, pRecord && (pRecord.Text || pRecord.Name)); } },
				});
			if (tmpV && (typeof tmpV.render === 'function')) { tmpV.render(); }
		}
		if (tmpCA.getElement('#ObservationList-InclusionPicker').length > 0)
		{
			const tmpV = tmpPicker.createPicker(`${this.providerName}-InclusionPicker`,
				{
					DestinationAddress: '#ObservationList-InclusionPicker', ValueAddress: `AppData.${this.providerName}.PickerInclusion`,
					Searchable: true, AllowClear: true, ClearLabel: 'Any', Placeholder: 'Any',
					DataProvider: this._inclusionDataProvider(), ResolveValue: (v) => Promise.resolve(this._inclusionOption(v)),
					OnChange: (v) => this.setInclusion(v == null ? '' : String(v)),
				});
			if (tmpV && (typeof tmpV.render === 'function')) { tmpV.render(); }
			if (tmpV && (typeof tmpV.setValue === 'function')) { tmpV.setValue(this._filter.Inclusion || ''); }
		}
	}

	_projectDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'Projects/Lite', IDField: 'IDProject',
				ToOption: (r) => ({ Value: Number(r.IDProject), Text: r.Value || r.Name || `Project ${r.IDProject}`, Record: r }),
			});
	}
	_projectResolveValue() { return (v) => { const id = Number(v); if (!id) { return Promise.resolve(null); } return this._get(`Project/${id}`).then((r) => ((r && r.IDProject) ? { Value: id, Text: r.Name || `#${id}` } : { Value: id, Text: `#${id}` })).catch(() => ({ Value: id, Text: `#${id}` })); }; }

	/** Distinct label VALUES for the currently-selected project + active tag type (freeform Tag strings). */
	_labelValueDataProvider()
	{
		return (pTerm) =>
		{
			const tmpProj = this._filter.IDProject; const tmpType = this._activeTagType;
			if (!tmpProj || !tmpType) { return Promise.resolve({ results: [], hasMore: false }); }
			const tmpTerm = String(pTerm == null ? '' : pTerm).replace(/~/g, '').trim();
			let flt = `FBV~IDProject~EQ~${tmpProj}~FBV~TagType~EQ~${encodeURIComponent(tmpType)}`;
			if (tmpTerm) { flt += `~FBV~Tag~LK~${encodeURIComponent(`%${tmpTerm}%`)}`; }
			return this._get(`ObservationTagProjectJoins/FilteredTo/${flt}~FSF~Tag~ASC~0/0/50`).then((rows) =>
			{
				const tmpSeen = {}; const tmpOut = [];
				(Array.isArray(rows) ? rows : []).forEach((r) => { const t = r.Tag; if (t && !tmpSeen[t]) { tmpSeen[t] = true; tmpOut.push({ Value: t, Text: t, Record: r }); } });
				return { results: tmpOut, hasMore: false };
			}).catch(() => ({ results: [], hasMore: false }));
		};
	}

	_authorDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._get(pURL), { Scope: 'FBV~Deleted~EQ~0', PageSize: 25 });
	}

	/** The fixed "In reports" (DocumentCount inclusion) vocabulary, served to a picker instead of a native <select>. */
	_INCLUSION_OPTIONS() { return [ { Value: '0', Text: 'Not in any report' }, { Value: '1', Text: 'In exactly one' }, { Value: '2', Text: 'In more than one' } ]; }
	_inclusionOption(pValue) { const v = String(pValue == null ? '' : pValue); return this._INCLUSION_OPTIONS().find((o) => o.Value === v) || null; }
	_inclusionDataProvider()
	{
		return (pTerm) =>
		{
			const t = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
			const rows = this._INCLUSION_OPTIONS().filter((o) => !t || o.Text.toLowerCase().indexOf(t) >= 0);
			return Promise.resolve({ results: rows, hasMore: false });
		};
	}
}

module.exports = ObservationListProvider;

module.exports.default_configuration = { ProviderIdentifier: 'ObservationList' };

// Categorical palette for the Charts view (curated mid-tones that read on the app's light theme).
const _CAT = [ '#2e7d74', '#3a86a8', '#6a6fae', '#9a6fb0', '#b5734e', '#4f9d69', '#c2708f', '#7f8b3d', '#c98a2e', '#5a8bce' ];
const _WARN = '#bd6631';
// Map view: how many of the most-recent filtered matches to fetch + plot. The match set can be in the millions, so we
// cap the plotted set (DateOfRecord-desc = "most recent N") and tell the user to refine the filter to see the rest.
const _MAP_FETCH = 2000;

const _CSS = /*css*/`
	/* Consistent horizontal gutter for the whole dashboard chrome (this screen runs full-bleed in Dashboard Mode, so the
	   page itself has no side padding). Standalone only — embedded in a Project tab the tab host supplies its own padding.
	   The wide results table full-bleeds back out to the edge (below) so it can still use the whole browser width. */
	.obl { width: 100%; --obl-gutter: clamp(1rem, 2.2vw, 2rem); }
	.obl:not(.obl-embedded) { padding-left: var(--obl-gutter); padding-right: var(--obl-gutter); }
	.obl.obl-embedded { --obl-gutter: 0px; }
	.obl-head { display: flex; align-items: baseline; justify-content: space-between; gap: 1rem; margin: 0 0 0.8rem; }
	.obl-title { font-size: 1.1rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); display: inline-flex; align-items: center; gap: 0.5rem; }
	.obl-title i { color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-count { font-size: 0.85rem; font-weight: 600; color: var(--theme-color-text-muted,#6b7686); }
	.obl-filters { background: var(--theme-color-background-secondary,#f4f6f9); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 10px; padding: 0.75rem 0.85rem; margin: 0 0 1rem; }
	.obl-row { display: flex; align-items: flex-end; gap: 0.6rem 0.8rem; flex-wrap: wrap; }
	.obl-row-pickers { margin-top: 0.6rem; }
	.obl-search { position: relative; flex: 1 1 18rem; min-width: 14rem; }
	.obl-search-ic { position: absolute; left: 0.6rem; top: 50%; transform: translateY(-50%); color: var(--theme-color-text-muted,#9aa3af); font-size: 0.8rem; }
	.obl-search-in { width: 100%; font-size: 0.88rem; padding: 0.42rem 0.6rem 0.42rem 1.9rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); }
	.obl-f { display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.82rem; }
	.obl-f-l { font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary,#45505f); }
	.obl-f input, .obl-f select { font-size: 0.85rem; padding: 0.38rem 0.5rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); }
	.obl-apply { font-size: 0.84rem; font-weight: 600; color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border: none; border-radius: 7px; padding: 0.42rem 0.85rem; cursor: pointer; }
	.obl-clear { font-size: 0.84rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: transparent; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; padding: 0.42rem 0.75rem; cursor: pointer; }
	.obl-pick { display: flex; flex-direction: column; gap: 0.15rem; flex: 1 1 16rem; min-width: 13rem; }
	.obl-picker { min-width: 12rem; }
	.obl-tags { display: flex; gap: 0.3rem; flex-wrap: wrap; margin-top: 0.3rem; }
	.obl-tag { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.76rem; font-weight: 600; color: var(--theme-color-text-primary,#1f2733); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 999px; padding: 0.08rem 0.2rem 0.08rem 0.5rem; }
	.obl-tag-x { border: none; background: transparent; color: var(--theme-color-text-muted,#98a2b3); cursor: pointer; font-size: 0.95rem; line-height: 1; }
	.obl-labelhint { margin-top: 0.6rem; font-size: 0.8rem; color: var(--theme-color-text-muted,#8894a4); display: flex; align-items: center; gap: 0.4rem; }
	.obl-labels-filter { margin-top: 0.6rem; display: flex; align-items: center; gap: 0.5rem 0.7rem; flex-wrap: wrap; }
	.obl-labeltype { font-size: 0.85rem; padding: 0.36rem 0.5rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); }
	.obl-labelpicker { min-width: 14rem; }
	.obl-matchall { font-size: 0.8rem; color: var(--theme-color-text-secondary,#45505f); display: inline-flex; align-items: center; gap: 0.3rem; cursor: pointer; }
	.obl-label-chips { display: flex; gap: 0.5rem; flex-wrap: wrap; flex-basis: 100%; }
	.obl-label-grp { display: inline-flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; }
	.obl-label-gt { font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted,#7a8794); }
	.obl-types { margin-top: 0.6rem; display: flex; align-items: baseline; gap: 0.6rem; }
	.obl-chips { display: flex; gap: 0.3rem; flex-wrap: wrap; }
	.obl-chip { font-size: 0.76rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 999px; padding: 0.15rem 0.6rem; cursor: pointer; }
	.obl-chip-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-chip-n { font-size: 0.68rem; font-weight: 800; opacity: 0.6; font-variant-numeric: tabular-nums; }
	.obl-chip-on .obl-chip-n { opacity: 0.85; }
	.obl-empty { padding: 2.4rem 1rem; text-align: center; color: var(--theme-color-text-muted,#8a94a3); font-size: 0.9rem; }
	.obl-empty.obl-error { color: var(--theme-color-status-danger,#c0392b); }
	.obl-spin { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-medium,#d5dbe3); border-top-color: var(--theme-color-brand-primary,#2e7d74); border-radius: 50%; animation: obl-sp 0.7s linear infinite; vertical-align: -2px; }
	@keyframes obl-sp { to { transform: rotate(360deg); } }
	.obl-listwrap { width: auto; overflow-x: auto; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 10px; }
	/* Wide tabular data uses the WHOLE browser width + scrolls: cancel the chrome gutter so the table band reaches the edges. */
	.obl:not(.obl-embedded) .obl-listwrap { margin-left: calc(-1 * var(--obl-gutter)); margin-right: calc(-1 * var(--obl-gutter)); border-left: none; border-right: none; border-radius: 0; }
	.obl-list { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
	.obl-list th { text-align: left; font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#6b7686); background: var(--theme-color-background-tertiary,#f4f6f9); padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light,#e8ebf0); white-space: nowrap; }
	.obl-list td { padding: 0.45rem 0.6rem; border-bottom: 1px solid var(--theme-color-border-light,#f1f4f8); vertical-align: middle; }
	.obl-r { cursor: pointer; }
	.obl-r:hover td { background: var(--theme-color-background-secondary,#f6f8fb); }
	.obl-thumb-cell { width: 3.4rem; }
	.obl-thumb { width: 3rem; height: 3rem; object-fit: cover; border-radius: 6px; border: 1px solid var(--theme-color-border-light,#e8ebf0); display: block; }
	.obl-thumb-broken, .obl-thumb-none { display: inline-flex; align-items: center; justify-content: center; color: var(--theme-color-text-muted,#c2c9d3); background: var(--theme-color-background-secondary,#eef1f6); font-size: 1rem; }
	.obl-thumb-broken { }
	.obl-type { font-size: 0.72rem; font-weight: 700; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border-radius: 5px; padding: 0.08rem 0.4rem; white-space: nowrap; }
	.obl-obscell { min-width: 16rem; } .obl-obsname { display: block; color: var(--theme-color-text-primary,#1f2733); } .obl-obsid { display: block; font-size: 0.72rem; color: var(--theme-color-text-muted,#9aa3af); }
	.obl-proj { color: var(--theme-color-text-secondary,#45505f); }
	.obl-nowrap { white-space: nowrap; }
	.obl-num { text-align: right; font-variant-numeric: tabular-nums; }
	.obl-pager { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 0.9rem; }
	.obl-pg { font-size: 0.84rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; padding: 0.35rem 0.7rem; cursor: pointer; }
	.obl-pg[disabled] { opacity: 0.45; cursor: default; }
	.obl-pg-info { font-size: 0.82rem; color: var(--theme-color-text-muted,#6b7686); font-variant-numeric: tabular-nums; }

	/* --- view switcher (List / Charts / Map) --- */
	.obl-viewtabs { display: inline-flex; gap: 0.15rem; padding: 0.2rem; background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 10px; margin: 0 0 1rem; }
	.obl-vtab { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#54636e); background: transparent; border: none; border-radius: 7px; padding: 0.38rem 0.85rem; cursor: pointer; }
	.obl-vtab i { font-size: 0.78rem; opacity: 0.8; }
	.obl-vtab:hover { color: var(--theme-color-text-primary,#1f2733); }
	.obl-vtab-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); box-shadow: 0 1px 2px rgba(20,40,50,0.12); }
	.obl-vtab-on i { opacity: 1; }

	/* --- charts view (facet panels) --- */
	.oxp { width: 100%; }
	.oxp-badge-row { display: flex; justify-content: flex-end; margin: 0 0 0.7rem; }
	.oxp-badge { display: inline-flex; align-items: center; gap: 0.5rem; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 999px; padding: 0.35rem 0.8rem; font-size: 0.76rem; color: var(--theme-color-text-muted,#6b7686); white-space: nowrap; }
	.oxp-badge b { color: var(--theme-color-brand-primary,#2e7d74); font-variant-numeric: tabular-nums; }
	.oxp-pulse { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: #2f8f5b; }
	.oxp-pulse-busy { background: var(--theme-color-brand-primary,#2e7d74); animation: oxp-blink 0.7s linear infinite; }
	@keyframes oxp-blink { 50% { opacity: 0.25; } }
	.oxp-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.85rem; margin: 0 0 0.9rem; }
	.oxp-kpi { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 13px; padding: 0.9rem 1rem; position: relative; overflow: hidden; }
	.oxp-eyebrow { font-size: 0.62rem; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; color: var(--theme-color-text-muted,#8b98a3); }
	.oxp-v { font-size: 1.85rem; font-weight: 800; line-height: 1; margin: 0.5rem 0 0.3rem; letter-spacing: -0.02em; color: var(--theme-color-text-primary,#1f2733); font-variant-numeric: tabular-nums; }
	.oxp-l { font-size: 0.76rem; color: var(--theme-color-text-muted,#6b7686); } .oxp-l-sub { color: var(--theme-color-text-muted,#9aa3af); font-variant-numeric: tabular-nums; }
	.oxp-kpi-warn .oxp-v { color: #bd6631; } .oxp-kpi-warn::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: #bd6631; }
	.oxp-grid { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 0.85rem; align-items: start; }
	.oxp-panel { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e2e8ec); border-radius: 13px; padding: 0.9rem 1rem 1rem; min-width: 0; }
	.oxp-ph { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; margin: 0 0 0.8rem; }
	.oxp-ph h3 { font-size: 0.92rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); display: flex; align-items: center; gap: 0.45rem; margin: 0; }
	.oxp-ph h3 i { color: var(--theme-color-brand-primary,#2e7d74); font-size: 0.85rem; }
	.oxp-hint { font-size: 0.68rem; color: var(--theme-color-text-muted,#9aa3af); }
	.oxp-rows { display: flex; flex-direction: column; gap: 0.15rem; }
	.oxp-row { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 0.6rem; padding: 0.32rem 0.5rem; border-radius: 8px; cursor: pointer; position: relative; border: 1px solid transparent; }
	.oxp-row:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.oxp-row-on { background: var(--theme-color-brand-primary-subtle,#e3f1ee); border-color: color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 32%, transparent); }
	.oxp-row-bg { position: absolute; left: 0.5rem; right: 0.5rem; top: 0.2rem; bottom: 0.2rem; border-radius: 6px; z-index: 0; transition: width 0.5s cubic-bezier(.22,1,.36,1); }
	.oxp-lab { display: flex; align-items: center; gap: 0.55rem; min-width: 0; z-index: 1; }
	.oxp-dot { width: 0.6rem; height: 0.6rem; border-radius: 3px; flex: none; }
	.oxp-name { font-size: 0.82rem; color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.oxp-row-on .oxp-name { font-weight: 600; color: var(--theme-color-brand-primary,#2e7d74); }
	.oxp-cnt { font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#54636e); font-variant-numeric: tabular-nums; z-index: 1; }
	.oxp-row-on .oxp-cnt { color: var(--theme-color-brand-primary,#2e7d74); }
	.oxp-track { position: absolute; left: 0.5rem; right: 0.5rem; bottom: 0.15rem; height: 2px; border-radius: 2px; background: var(--theme-color-border-light,#eef2f4); overflow: hidden; z-index: 0; }
	.oxp-fill { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 2px; transition: width 0.5s cubic-bezier(.22,1,.36,1); }
	.oxp-stack { display: flex; height: 2.4rem; border-radius: 9px; overflow: hidden; border: 1px solid var(--theme-color-border-light,#e2e8ec); margin: 0 0 0.7rem; }
	.oxp-seg { height: 100%; cursor: pointer; transition: width 0.5s cubic-bezier(.22,1,.36,1), opacity 0.15s; min-width: 2px; }
	.oxp-seg:hover { filter: brightness(1.07); } .oxp-seg-dim { opacity: 0.3; }
	.oxp-flegend { display: flex; flex-direction: column; gap: 0.25rem; }
	.oxp-fl { display: grid; grid-template-columns: auto 1fr auto auto; align-items: center; gap: 0.6rem; padding: 0.3rem 0.5rem; border-radius: 8px; cursor: pointer; font-size: 0.82rem; border: 1px solid transparent; }
	.oxp-fl:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.oxp-fl-on { background: var(--theme-color-brand-primary-subtle,#e3f1ee); border-color: color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 30%, transparent); }
	.oxp-sw { width: 0.7rem; height: 0.7rem; border-radius: 3px; } .oxp-fname { color: var(--theme-color-text-primary,#1f2733); }
	.oxp-fpct { color: var(--theme-color-text-muted,#9aa3af); font-variant-numeric: tabular-nums; } .oxp-fcnt { font-weight: 600; font-variant-numeric: tabular-nums; color: var(--theme-color-text-secondary,#54636e); }
	.oxp-callout { margin-top: 0.75rem; display: flex; gap: 0.6rem; align-items: flex-start; background: #f5e7db; border: 1px solid color-mix(in srgb, #bd6631 26%, transparent); border-radius: 10px; padding: 0.6rem 0.75rem; }
	.oxp-callout i { color: #bd6631; margin-top: 0.1rem; } .oxp-callout p { margin: 0; font-size: 0.76rem; color: var(--theme-color-text-primary,#1f2733); } .oxp-callout b { color: #bd6631; }
	.oxp-callout a { color: var(--theme-color-brand-primary,#2e7d74); font-weight: 600; text-decoration: none; white-space: nowrap; }
	.oxp-foot { margin-top: 1rem; font-size: 0.72rem; color: var(--theme-color-text-muted,#9aa3af); line-height: 1.6; } .oxp-foot i { color: #2f8f5b; } .oxp-foot b { color: var(--theme-color-text-secondary,#54636e); }

	/* --- map view (Leaflet + docked responsive sidebar) --- */
	.obl-maprow { display: grid; grid-template-columns: 1fr 22rem; gap: 0.9rem; align-items: start; }
	.obl-map { height: 70vh; min-height: 30rem; border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 12px; overflow: hidden; position: relative; z-index: 0; }
	.obl-side { align-self: start; min-width: 0; }
	.obl-mut { color: var(--theme-color-text-muted,#9aa3af); }
	.obl-side-sum-wrap:empty { display: none; }
	.obl-side-sum { font-size: 0.8rem; line-height: 1.4; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#f4f6f9); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 9px; padding: 0.5rem 0.6rem; margin: 0 0 0.65rem; }
	.obl-side-sum b { color: var(--theme-color-brand-primary,#2e7d74); font-variant-numeric: tabular-nums; }
	.obl-side-hint { font-size: 0.76rem; color: var(--theme-color-text-muted,#8a94a3); padding: 0.55rem 0.2rem 0; display: flex; align-items: center; gap: 0.4rem; }
	.obl-side-hint i { color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-legend { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 12px; padding: 0.75rem 0.85rem; }
	.obl-legend-h { display: flex; align-items: baseline; justify-content: space-between; gap: 0.5rem; font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#8b98a3); margin: 0 0 0.5rem; }
	.obl-legend-h .obl-mut { text-transform: none; letter-spacing: 0; font-weight: 600; font-size: 0.68rem; }
	.obl-legend-list { display: flex; flex-direction: column; gap: 0.15rem; max-height: 52vh; overflow: auto; }
	.obl-legend-i { display: flex; align-items: center; gap: 0.5rem; font-size: 0.82rem; padding: 0.28rem 0.4rem; border-radius: 7px; cursor: pointer; border: 1px solid transparent; }
	.obl-legend-i:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.obl-legend-on { background: var(--theme-color-brand-primary-subtle,#e3f1ee); border-color: color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 30%, transparent); }
	.obl-legend-dot { width: 0.7rem; height: 0.7rem; border-radius: 3px; flex: none; }
	.obl-legend-v { flex: 1; min-width: 0; color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.obl-legend-on .obl-legend-v { font-weight: 600; color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-legend-c { font-size: 0.78rem; font-weight: 700; color: var(--theme-color-text-secondary,#54636e); font-variant-numeric: tabular-nums; }
	/* docked detail panel */
	.obl-side-detail { background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 12px; display: flex; flex-direction: column; overflow: hidden; max-height: 74vh; }
	.obl-side-top { position: relative; padding: 0.7rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light,#e8ebf0); flex: 0 0 auto; }
	.obl-side-idrow { display: flex; align-items: flex-start; gap: 0.6rem; padding-right: 1.4rem; }
	.obl-side-thumb { width: 3.2rem; height: 3.2rem; flex: 0 0 auto; object-fit: cover; border-radius: 8px; border: 1px solid var(--theme-color-border-light,#e8ebf0); display: block; }
	.obl-side-thumb-none, .obl-side-thumb-broken { display: inline-flex; align-items: center; justify-content: center; background: var(--theme-color-background-secondary,#eef1f6); color: var(--theme-color-text-muted,#c2c9d3); font-size: 1.1rem; }
	.obl-side-idtext { min-width: 0; flex: 1; }
	.obl-side-name { display: block; font-size: 0.9rem; font-weight: 800; line-height: 1.28; color: var(--theme-color-text-primary,#1f2733); word-break: break-word; }
	.obl-side-x { position: absolute; top: 0.55rem; right: 0.55rem; border: none; background: transparent; color: var(--theme-color-text-muted,#98a2b3); cursor: pointer; font-size: 1rem; line-height: 1; padding: 0.1rem 0.2rem; }
	.obl-side-x:hover { color: var(--theme-color-text-primary,#1f2733); }
	.obl-side-sub { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem 0.5rem; margin-top: 0.35rem; font-size: 0.74rem; }
	.obl-side-scroll { padding: 0.75rem 0.8rem; overflow: auto; flex: 1 1 auto; }
	.obl-props { display: grid; grid-template-columns: repeat(auto-fill, minmax(8.5rem, 1fr)); gap: 0.55rem 0.7rem; }
	.obl-side-data { margin-top: 0.9rem; border-top: 1px solid var(--theme-color-border-light, #eef1f5); padding-top: 0.6rem; }
	.obl-side-data-h { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #8a94a3); margin-bottom: 0.4rem; }
	.obl-side-data .hor-slots { grid-template-columns: 1fr; }
	.obl-note { color: var(--theme-color-text-muted, #8a94a3); font-size: 0.8rem; }
	.obl-prop-type { display: inline-flex; align-items: center; gap: 0.4rem; }
	.obl-prop { min-width: 0; }
	.obl-prop-l { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted,#8b98a3); line-height: 1.2; }
	.obl-prop-v { font-size: 0.86rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); word-break: break-word; margin-top: 0.1rem; }
	.obl-prop-u { font-size: 0.7rem; font-weight: 600; color: var(--theme-color-text-muted,#9aa3af); }
	.obl-rep { display: inline-block; font-size: 0.74rem; font-weight: 700; border-radius: 5px; padding: 0.05rem 0.4rem; }
	.obl-rep-0 { color: #bd6631; background: #f5e7db; }
	.obl-rep-1 { color: var(--theme-color-brand-primary,#2e7d74); background: var(--theme-color-brand-primary-subtle,#e3f1ee); }
	.obl-side-foot { padding: 0.6rem 0.8rem; border-top: 1px solid var(--theme-color-border-light,#e8ebf0); flex: 0 0 auto; }
	.obl-side-btn { display: flex; align-items: center; justify-content: center; gap: 0.4rem; width: 100%; font-size: 0.82rem; font-weight: 600; color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-radius: 7px; padding: 0.45rem 0.75rem; text-decoration: none; }
	.obl-side-btn:hover { filter: brightness(1.05); }
	@media (max-width: 900px) { .obl-maprow { grid-template-columns: 1fr; } .obl-side-detail { max-height: none; } .obl-map { height: 55vh; min-height: 22rem; } }

	/* --- view-tabs row (tabs left, live count right) --- */
	.obl-viewtabs-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin: 0 0 1rem; flex-wrap: wrap; }
	.obl-viewtabs { margin: 0; }
	.obl-vtabcount { font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-muted,#6b7686); font-variant-numeric: tabular-nums; }
	.obl-vtabs-right { display: flex; align-items: center; gap: 0.55rem 0.9rem; flex-wrap: wrap; }
	.obl-embedded .obl-filters { margin-top: 0; }

	/* --- full-screen toggle + overlay --- */
	.obl-fsbtn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 8px; padding: 0.36rem 0.7rem; cursor: pointer; white-space: nowrap; }
	.obl-fsbtn:hover { border-color: var(--theme-color-brand-primary,#2e7d74); color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-fsbtn i { font-size: 0.78rem; opacity: 0.85; }
	.obl-fsbtn-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-fsbtn-on:hover { color: #fff; filter: brightness(1.05); }
	.obl-fsbtn-on i { opacity: 1; }
	/* The overlay fills the whole browser view, above the app nav (Bulma navbar z-index is 30). No ancestor sets a
	   transform/filter/contain, so position:fixed anchors to the viewport. It keeps the same side gutter as normal. */
	.obl.obl-fs { position: fixed; inset: 0; z-index: 1100; overflow: auto; background: var(--theme-color-background-tertiary,#f3f4f6); padding-top: 1.1rem; padding-bottom: 1.6rem; transform: translateZ(0); }

	/* --- Observation Types dropdown-checklist --- */
	.obl-typedd { position: relative; display: inline-block; }
	.obl-ddbtn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.85rem; padding: 0.38rem 0.6rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); color: var(--theme-color-text-primary,#1f2733); cursor: pointer; white-space: nowrap; min-width: 8.5rem; justify-content: space-between; }
	.obl-ddbtn:hover { border-color: var(--theme-color-brand-primary,#2e7d74); }
	.obl-ddbtn-open { border-color: var(--theme-color-brand-primary,#2e7d74); box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-color-brand-primary,#2e7d74) 18%, transparent); }
	.obl-ddbtn-l { overflow: hidden; text-overflow: ellipsis; }
	.obl-ddcaret { font-size: 0.7rem; color: var(--theme-color-text-muted,#8894a4); }
	.obl-ddbadge { background: var(--theme-color-brand-primary,#2e7d74); color: #fff; font-size: 0.66rem; font-weight: 700; border-radius: 999px; padding: 0.02rem 0.4rem; font-variant-numeric: tabular-nums; }
	.obl-ddpanel { position: absolute; top: calc(100% + 0.35rem); left: 0; z-index: 40; width: 19rem; max-width: 82vw; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 9px; box-shadow: 0 12px 30px -12px rgba(20,40,50,0.3); overflow: hidden; }
	.obl-ddpanel-head { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light,#eef2f4); font-size: 0.66rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#8b98a3); }
	.obl-ddclearbtn { border: none; background: transparent; color: var(--theme-color-brand-primary,#2e7d74); font: inherit; font-size: 0.7rem; font-weight: 700; cursor: pointer; text-transform: none; letter-spacing: 0; }
	.obl-ddclearbtn:hover { text-decoration: underline; }
	.obl-ddlist { max-height: 17rem; overflow-y: auto; padding: 0.3rem; }
	.obl-dditem { display: flex; align-items: center; gap: 0.55rem; padding: 0.35rem 0.5rem; border-radius: 6px; cursor: pointer; font-size: 0.85rem; }
	.obl-dditem:hover { background: var(--theme-color-background-secondary,#f5f8f9); }
	.obl-dditem input { accent-color: var(--theme-color-brand-primary,#2e7d74); width: 0.95rem; height: 0.95rem; cursor: pointer; flex: none; }
	.obl-ddname { flex: 1; color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
	.obl-dditem-on .obl-ddname { font-weight: 600; }
	.obl-ddcount { font-size: 0.74rem; color: var(--theme-color-text-muted,#9aa3af); font-variant-numeric: tabular-nums; }
	.obl-ddempty { padding: 0.6rem; color: var(--theme-color-text-muted,#9aa3af); font-size: 0.82rem; text-align: center; }

	@media (max-width: 900px) { .oxp-kpis { grid-template-columns: repeat(2, 1fr); } .oxp-grid { grid-template-columns: 1fr; } }
	@media (prefers-reduced-motion: reduce) { .oxp-fill, .oxp-seg, .oxp-row-bg { transition: none; } .oxp-pulse-busy { animation: none; } }
`;
